import React from 'react'
import styled, { css } from 'styled-components'

import CSSType from 'csstype'

import { Breakpoints, Margins, Colors } from '../../constants'

import { Spinner, Row } from '../index'

import Arrow from '../../assets/arrow.svg'

const whiteDropShadow = css`
  -webkit-box-shadow: 0px 0px 20px 0px ${Colors.WHITE};
  -moz-box-shadow: 0px 0px 20px 0px ${Colors.WHITE};
  box-shadow: 0px 0px 20px 0px ${Colors.WHITE};
`

const blueDropShadow = css`
  -webkit-box-shadow: 0px 0px 20px 0px ${Colors.BLUE};
  -moz-box-shadow: 0px 0px 20px 0px ${Colors.BLUE};
  box-shadow: 0px 0px 20px 0px ${Colors.BLUE};
`

type ButtonBreakpoint = 'mobile' | 'tablet' | 'desktop'
type ButtonType = 'primary' | 'secondary' | 'external'
type ButtonState = 'default' | 'error' | 'success'
type ButtonTheme = 'blue' | 'white'
type ButtonSize = 's' | 'm' | 'l'

const ButtonSizes = {
  s: {
    desktop: {
      width: 145,
      height: 52,
      borderRadius: 12,
      fontSize: 14,
      fontWeight: 600,
    },
    tablet: {
      width: 130,
      height: 46,
      borderRadius: 12,
      fontSize: 12,
      fontWeight: 600,
    },
    mobile: {
      width: 117,
      height: 41,
      borderRadius: 12,
      fontSize: 12,
      fontWeight: 600,
    },
  },
  m: {
    desktop: {
      width: 162,
      height: 58,
      borderRadius: 12,
      fontSize: 16,
      fontWeight: 600,
    },
    tablet: {
      width: 145,
      height: 52,
      borderRadius: 12,
      fontSize: 14,
      fontWeight: 600,
    },
    mobile: {
      width: 130,
      height: 46,
      borderRadius: 12,
      fontSize: 14,
      fontWeight: 600,
    },
  },
  l: {
    desktop: {
      width: 165,
      height: 60,
      borderRadius: 12,
      fontSize: 16,
      fontWeight: 600,
    },
    tablet: {
      width: 140,
      height: 50,
      borderRadius: 12,
      fontSize: 14,
      fontWeight: 600,
    },
    mobile: {
      width: 130,
      height: 45,
      borderRadius: 10,
      fontSize: 14,
      fontWeight: 600,
    },
  },
}

const getButtonSizeCss = (
  size: ButtonSize,
  breakpoint: ButtonBreakpoint,
  resizable = false,
  fullWidth = false,
) => {
  return `
        border-radius: ${ButtonSizes[size][breakpoint].borderRadius}px; 
        min-width: ${ButtonSizes[size][breakpoint].width}px; 
        height: ${ButtonSizes[size][breakpoint].height}px;
        font-size: ${ButtonSizes[size][breakpoint].fontSize}px;
        font-weight: ${ButtonSizes[size][breakpoint].fontWeight};
        ${resizable ? `min-width: unset; padding: 0px 20px;` : ``}
        ${fullWidth ? `width: 100%;` : ``}
  `
}

const getButtonThemeCss = (
  type: ButtonType,
  theme: ButtonTheme,
  disabled?: boolean,
) => {
  if (type === 'external') {
    return css`
      background-color: transparent;
      color: ${Colors.WHITE};
    `
  }
  switch (theme) {
    case 'white':
      return type === 'primary'
        ? css`
            background-color: ${Colors.WHITE};
            ${disabled ? `background-color: ${Colors.SILVER};` : ``}
            transition: 0.25s;
            &:hover {
              ${disabled ? '' : whiteDropShadow}
            }
          `
        : css`
            background-color: transparent;
            border: 1px solid #fff;
            color: #fff;
            transition: 0.25s;
            &:hover {
              background-color: ${Colors.WHITE};
              color: ${Colors.BLACK};
              ${disabled ? '' : whiteDropShadow}
            }
          `
    case 'blue':
      return type === 'primary'
        ? css`
            background-color: ${Colors.BLUE};
            color: ${Colors.WHITE};
            &:hover {
              ${disabled ? '' : blueDropShadow}
            }
            ${disabled ? `background-color: ${Colors.PALE_BLUE};` : ``}
          `
        : css`
            background-color: transparent;
            border: 1px solid ${Colors.BLUE};
            color: #fff;
            &:hover {
              background-color: ${Colors.BLUE};
              color: ${Colors.WHITE};
              ${disabled ? '' : blueDropShadow}
            }
          `
    default:
      return type === 'primary'
        ? css`
            background-color: ${Colors.WHITE};
          `
        : css`
            background-color: transparent;
            border: 1px solid ${Colors.WHITE};
            color: ${Colors.WHITE};
            &:hover {
            }
          `
  }
}

interface StyledButtonProps {
  buttonType?: ButtonType
  state?: ButtonState
  theme?: ButtonTheme
  size?: ButtonSize
  disabled?: boolean
  loading?: boolean
  resizable?: boolean
  fullWidth?: boolean
  backgroundColor?: CSSType.Property.BackgroundColor
}

const StyledButton = styled.button<StyledButtonProps>`
  border: 0;
  padding: 8px 8px 8px 8px;
  cursor: pointer;

  ${(p) => css`
    // Mobile
    @media only screen and (max-width: ${Breakpoints.MOBILE}px) {
      ${getButtonSizeCss(p.size, 'mobile', p.resizable, p.fullWidth)}
    }

    // Tablet
    @media only screen and (min-width: ${Breakpoints.MOBILE}px) and (max-width: ${Breakpoints.TABLET_LANDSCAPE}px) {
      ${getButtonSizeCss(p.size, 'tablet', p.resizable, p.fullWidth)}
    }

    // Tablet Landscape
    @media only screen and (min-width: ${Breakpoints.TABLET_LANDSCAPE}px) {
      ${getButtonSizeCss(p.size, 'desktop', p.resizable, p.fullWidth)}
    }

    ${getButtonThemeCss(p.buttonType, p.theme, p.disabled)}

    ${p.backgroundColor
      ? `background-color: ${p.backgroundColor} !important;`
      : ``}
  `}
`

const ArrowIcon = styled.img`
  margin-left: ${Margins.s}px;
`

const Link = styled.a`
  color: inherit;
  text-decoration: none;
`

interface ButtonProps {
  buttonType?: ButtonType
  state?: ButtonState
  theme?: ButtonTheme
  size?: ButtonSize
  children?: React.ReactNode
  text?: string
  href?: string
  disabled?: boolean
  loading?: boolean
  fullWidth?: boolean
  resizable?: boolean
  onClick?: any
  backgroundColor?: CSSType.Property.BackgroundColor
}

export const Button = ({
  buttonType = 'primary',
  state = 'default',
  theme = 'blue',
  size = 'm',
  loading = false,
  disabled = false,
  resizable = false,
  fullWidth = false,
  backgroundColor,
  text,
  children,
  onClick,
  href,
}: ButtonProps) => {
  const buttonText = text ? text : children
  return (
    <StyledButton
      onClick={onClick}
      loading={loading}
      disabled={disabled}
      resizable={resizable}
      buttonType={buttonType}
      state={state}
      theme={theme}
      size={size}
      fullWidth={fullWidth}
      backgroundColor={backgroundColor}
    >
      {buttonType === 'external' ? (
        <Link href={href} target="_blank" rel="noreferrer">
          {loading ? (
            <Row alignItems="center" justifyContent="center">
              <Spinner />
            </Row>
          ) : (
            buttonText
          )}
          <ArrowIcon src={Arrow} />
        </Link>
      ) : (
        <>
          {href ? (
            <Link target="_blank" href={href}>
              {loading ? (
                <Row alignItems="center" justifyContent="center">
                  <Spinner />
                </Row>
              ) : (
                buttonText
              )}
            </Link>
          ) : (
            <>
              {loading ? (
                <Row alignItems="center" justifyContent="center">
                  <Spinner />
                </Row>
              ) : (
                buttonText
              )}
            </>
          )}
        </>
      )}
    </StyledButton>
  )
}
